import h from "trader-hyperscript";
import { Remote } from "trader.ts";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { renderRemote } from "trader.ts/ui.common";
import { Form, textBox } from "trader.ts/ui.forms";
import { match } from "ts-pattern";
import { makeGradient, primaryGreyPlanningColors } from "../lib/colors";
import { renderClosePopupButton } from "../pages/planning/selectionandforms";
import { filter_id, filter_type } from "./../../sql/classy/types";
import * as FA from "./../../styles/fontawesome.css.classes";
import * as style from "./../../styles/style.css.classes";
import * as routes from "./../routes";
import { Filter } from "./filterbuild";
import { renderPopupModal } from "./popup";
import { renderSaveButton } from "./savebutton";

type filtersImpl<
  FILTER_TYPE,
  SAVED_FILTER extends { value: string; name: string },
> = {
  getFiltersForType: (t: FILTER_TYPE) => Promise<SAVED_FILTER[]>;
  deleteFilter: (f: SAVED_FILTER) => Promise<{ tag: "success" }>;
  saveFilter: (f: {
    name: string;
    value: string;
    type: FILTER_TYPE;
  }) => Promise<{ tag: "success" }>;
};

export const classyFiltersImpl: filtersImpl<
  filter_type,
  {
    id: filter_id;
    type: filter_type;
    value: string;
    name: string;
  }
> = {
  getFiltersForType: (type) =>
    rpc(routes.getFiltersForTypeSpec, undefined, { type }),
  deleteFilter: (f) => rpc(routes.deleteFilterSpec, undefined, { id: f.id }),
  saveFilter: (values) =>
    rpc(routes.saveFilterSpec, undefined, {
      name: values.name,
      value: values.value,
      type: values.type,
    }),
};

export function renderFiltersSaveAndLoadButtons<
  T,
  SAVED_FILTER extends { value: string; name: string },
  FILTER_TYPE,
>(
  filtersImpl: filtersImpl<FILTER_TYPE, SAVED_FILTER>,
  filterInstance: Filter<T>,
  type: FILTER_TYPE,
  filterlabel: string,
) {
  const showLoadPopupS = new Source(false);
  const showSavePopupS = new Source(false);

  function renderLoadPopup(opts: { close: () => void }): HTMLElement {
    const filtersRS = new Source({
      tag: "initial",
    } as Remote.Remote<SAVED_FILTER[]>);

    function loadFilters() {
      void filtersImpl
        .getFiltersForType(type)
        .then((res) => filtersRS.set({ tag: "loaded", item: res }));
    }
    loadFilters();

    function loadFilter(s: string) {
      filterInstance.applySerializedState(JSON.parse(s));
      opts.close();
    }

    async function removeFilter(f: SAVED_FILTER) {
      await filtersImpl.deleteFilter(f);
      loadFilters();
    }

    return (
      <div className={style.panel} style="min-height: 400px;">
        <div
          className={style.panel_header}
          style={`background: ${makeGradient(
            primaryGreyPlanningColors,
          )}; color: ${primaryGreyPlanningColors.text};`}
        >
          {"Filter laden"}
          {renderClosePopupButton(opts.close)}
        </div>
        {renderRemote(filtersRS, (filters) =>
          filters.length === 0 ? (
            <div style="text-align: center; font-style: italic;">
              {"Je hebt nog geen filters voor %filterlabel".replace(
                "%filterlabel",
                filterlabel,
              )}
            </div>
          ) : (
            filters
              .orderBy((f) => f.name)
              .map((f) => (
                <div style="display: flex;">
                  <a
                    onclick={() => loadFilter(f.value)}
                    className={style.input_with_icon}
                    style="flex: 1; margin-bottom: 8px;"
                  >
                    <i className={FA.icon_check_solid}></i>
                    <div
                      className={style.input_like}
                      style="background-color: var(--grey-000); margin: 0px;"
                    >
                      {f.name}
                    </div>
                  </a>
                  <button
                    className={style.button_has_only_icon + style.button_small}
                    style="height: var(--input-height); margin-left: 8px;"
                    onclick={() => removeFilter(f)}
                  >
                    <i className={FA.icon_trash_alt_solid}></i>
                  </button>
                </div>
              ))
          ),
        )}
      </div>
    );
  }

  function renderSavePopup(opts: { close: () => void }): HTMLElement {
    const form = new Form({})
      .addField("name", [], () => textBox({ label: "Naam" }))
      .build();

    return (
      <div className={style.panel}>
        <div
          className={style.panel_header}
          style={`background: ${makeGradient(
            primaryGreyPlanningColors,
          )}; color: ${primaryGreyPlanningColors.text};`}
        >
          {"Filter opslaan"}
          {renderClosePopupButton(opts.close)}
        </div>
        <section>{form.render()}</section>
        <div style="text-align: right;">
          {renderSaveButton({
            formS: form.s,
            save: (values) =>
              filtersImpl.saveFilter({
                name: values.name,
                value: JSON.stringify(filterInstance.serializeState()),
                type,
              }),
            reload: opts.close,
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style="text-align: right">
        <button onclick={() => showLoadPopupS.set(true)}>
          <i className={FA.icon_cog_solid}></i>
          {"Opgeslagen filters"}
        </button>
        <button
          onclick={() => showSavePopupS.set(true)}
          className={style.button_accent}
        >
          <i className={FA.icon_save_solid}></i>
          {"Sla filter op"}
        </button>
      </div>
      {renderPopupModal(showLoadPopupS, renderLoadPopup)}
      {renderPopupModal(showSavePopupS, renderSavePopup)}
    </div>
  );
}

function renderFilterType(type: filter_type): string {
  return match(type)
    .with("Student", () => "studenten")
    .with("Enrollment", () => "inschrijvingen")
    .exhaustive();
}
